import type { SourceDocument, SourceDocumentKind } from './types';

export const SOURCE_DOCUMENT_BACKGROUND = '#ffffff';

export const normalizeSourceText = (value: string) => value.replace(/\s+/g, ' ').trim();

export const escapeSourceHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

const sourceKindLabels: Record<SourceDocumentKind, string> = {
    ipo: 'IPO Event',
    'merger-acquisition': 'M&A Filing',
    news: 'News Source',
    'press-release': 'Press Release',
    source: 'Source Document',
};

export const getSourceKindLabel = (source: SourceDocument) => source.label || sourceKindLabels[source.kind];

const entityInitials = (value: string) =>
    value
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');

const safeHttpUrl = (value?: string) => {
    if (!value) {return undefined;}
    try {
        const normalized = /^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`;
        const url = new URL(normalized);
        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : undefined;
    } catch {
        return undefined;
    }
};

export const buildInlineSourceDocument = (source: SourceDocument) => {
    const metadata = [source.publisher, source.publishedAt].filter(Boolean).map(String);
    const paragraphs = String(source.summary ?? '')
        .split(/\n{2,}/)
        .filter(Boolean)
        .map((paragraph) => `<p>${escapeSourceHtml(paragraph)}</p>`)
        .join('');

    return [
        '<!doctype html><html><head><meta charset="utf-8">',
        '<style>:root{color-scheme:only light}*{box-sizing:border-box}html,body{background:#fff;color:#1f1f1f;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}body{margin:0 auto;max-width:920px;padding:2rem;font-size:16px;line-height:1.65}h1{line-height:1.25}.metadata{color:#737373;font-size:14px;margin-bottom:2rem}</style>',
        '</head><body>',
        `<h1>${escapeSourceHtml(source.title)}</h1>`,
        metadata.length ? `<div class="metadata">${metadata.map(escapeSourceHtml).join(' · ')}</div>` : '',
        paragraphs || '<p>Additional source details are unavailable.</p>',
        '</body></html>',
    ].join('');
};

export const getSourceQuoteHtml = (source: SourceDocument, quote: string, sourceUrl: string) => {
    const entityName = source.sourceEntity?.name?.trim() || source.publisher?.trim() || source.title;
    const logoUrl = safeHttpUrl(source.sourceEntity?.logoUrl?.trim());
    const website = safeHttpUrl(source.sourceEntity?.website?.trim());
    const logoAttribute = logoUrl ? ` data-logo-url="${escapeSourceHtml(logoUrl)}"` : '';
    const websiteAttribute = website ? ` data-website="${escapeSourceHtml(website)}"` : '';
    const citationLabel = getSourceKindLabel(source);

    return [
        '<blockquote class="blockquote-light filing-source-quote" contenteditable="false">',
        `<p class="filing-source-quote-text" contenteditable="false" data-filing-quote-text="true">${escapeSourceHtml(normalizeSourceText(quote))}</p>`,
        '<p class="filing-source-citation">',
        `<a class="filing-source-button" data-filing-source="true" data-source-document="true" data-source-kind="${escapeSourceHtml(source.kind)}" data-avatar="${escapeSourceHtml(entityInitials(entityName) || 'S')}" data-company="${escapeSourceHtml(entityName)}" data-filing="${escapeSourceHtml(citationLabel)}" href="${escapeSourceHtml(sourceUrl)}"${logoAttribute}${websiteAttribute}>`,
        `<span class="filing-source-company">${escapeSourceHtml(entityName)}</span>`,
        '<span aria-hidden="true" class="filing-source-chevron"></span>',
        `<span class="filing-source-filing">${escapeSourceHtml(citationLabel)}</span>`,
        '</a></p></blockquote><p></p>',
    ].join('');
};

export const getSourceDocumentLink = (source: SourceDocument, quote?: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sourceDocumentTitle', source.title);
    url.searchParams.set('sourceDocumentKind', source.kind);
    url.searchParams.set('sourceDocumentLabel', source.label);
    if (source.canonicalUrl) {url.searchParams.set('sourceDocumentUrl', source.canonicalUrl);}
    if (source.publisher) {url.searchParams.set('sourceDocumentPublisher', source.publisher);}
    if (source.publishedAt) {url.searchParams.set('sourceDocumentDate', source.publishedAt);}
    if (source.summary)
        {url.searchParams.set('sourceDocumentSummary', normalizeSourceText(source.summary).slice(0, 500));}
    if (source.sourceEntity?.name) {url.searchParams.set('sourceDocumentEntity', source.sourceEntity.name);}
    if (source.sourceEntity?.logoUrl) {url.searchParams.set('sourceDocumentLogo', source.sourceEntity.logoUrl);}
    if (source.sourceEntity?.website) {url.searchParams.set('sourceDocumentWebsite', source.sourceEntity.website);}
    if (quote) {url.searchParams.set('sourceDocumentText', normalizeSourceText(quote).slice(0, 240));}
    return url.toString();
};

export const clearSourceDocumentParams = (search: string) => {
    const params = new URLSearchParams(search);
    [...params.keys()].filter((key) => key.startsWith('sourceDocument')).forEach((key) => params.delete(key));
    const next = params.toString();
    return next ? `?${next}` : '';
};

export const getLinkedSourceDocument = (search: string): SourceDocument | undefined => {
    const params = new URLSearchParams(search);
    const title = params.get('sourceDocumentTitle');
    const kind = params.get('sourceDocumentKind') as SourceDocumentKind | null;
    if (!title || !kind || !(kind in sourceKindLabels)) {return undefined;}
    const entityName = params.get('sourceDocumentEntity');
    return {
        canonicalUrl: params.get('sourceDocumentUrl') ?? undefined,
        kind,
        label: params.get('sourceDocumentLabel') || sourceKindLabels[kind],
        publishedAt: params.get('sourceDocumentDate') ?? undefined,
        publisher: params.get('sourceDocumentPublisher') ?? undefined,
        sourceEntity: entityName
            ? {
                  logoUrl: params.get('sourceDocumentLogo') ?? undefined,
                  name: entityName,
                  website: params.get('sourceDocumentWebsite') ?? undefined,
              }
            : undefined,
        summary: params.get('sourceDocumentSummary') ?? undefined,
        title,
    };
};

export const applyLightSourceDocumentSurface = (document: Document) => {
    [document.documentElement, document.body].filter(Boolean).forEach((surface) => {
        surface.style.setProperty('background-color', SOURCE_DOCUMENT_BACKGROUND, 'important');
        surface.style.setProperty('color-scheme', 'only light', 'important');
    });
};
